
import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { MerchantsService } from '../../core/services/merchants.service';
import { Router } from '@angular/router';
import { StoresService } from '../../core/services/stores.service';
import { UserService } from '../../core/services/user.service';
import { ToolsService } from '../../core/services/tools.service';

import { AuthService } from '../../core/services/auth.service';

import { CurrencyService } from '../../core/services/currency.service';
import { TranslateService } from '@ngx-translate/core';
import { CountryService } from '../../core/services/country.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ChangeData } from 'untold-ngx-intl-tel-input';
import { isPlatformBrowser } from '@angular/common';
import { IntlService } from '../../core/services/intl.service';
import { filter, take } from 'rxjs';
declare const fbq;

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-create-merchant',
  templateUrl: './create-merchant.component.html',
  styleUrls: ['./create-merchant.component.scss'],
})
export class CreateMerchantComponent implements OnInit {
  public form: UntypedFormGroup;

  public submitAttempted = false;
  public submitSocialAttempted = false;
  public loading = false;
  public errorMsg: string;

  public currentTimezone;
  public countries = [];
  public showCountrySelector = false;
  public slugError;
  public slugValid;
  public validatingSlug = false;

  public currentUser;

  public extraData;
  public loadingGoogle = true;
  public currentStep = 1;
  public waitForGoogleRetries = 0;

  constructor(
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private merchantsService: MerchantsService,
    private toolsService: ToolsService,
    private storeService: StoresService,
    private analytics: AnalyticsService,
    private userService: UserService,
    private router: Router,
    private currencyService: CurrencyService,
    private translate: TranslateService,
    private countryService: CountryService,
    @Inject(PLATFORM_ID) protected platformId,
    private intlService: IntlService,
    private ref: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.currentTimezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
    this.countries = this.countryService.getCountries();

    let user = null;

    this.userService.currentUser.pipe(filter(v => v ? true : false), take(1)).subscribe((res) => {
      this.currentUser = res;
    });

    const socialEmailEmpty = user && JSON.parse(user) && JSON.parse(user).socialEmailEmpty;

    this.generateForm(socialEmailEmpty);

    this.analytics.recordPosthogSessionStart();

    this.waitForGoogle();
  }

  waitForGoogle() {
    // wait for "google" to be defined in the window object
    if (typeof google === 'undefined' || typeof google?.maps?.places === 'undefined') {
      setTimeout(() => {
        this.waitForGoogleRetries++;
        if (this.waitForGoogleRetries > 20) {
          this.loadingGoogle = false;
          this.currentStep = 2;
          return;
        }
        this.waitForGoogle();
      }, 100);
    } else {
      this.loadingGoogle = false;
    }
  }

  generateForm(socialEmailEmpty = false) {
    const currentCountry = this.intlService.getCurrentCountry();
    this.showCountrySelector = false;
    let startCountry;
    switch (currentCountry) {
      case 'pt':
        startCountry = 'Portugal';
        break;
      case 'es':
        startCountry = 'Spain';
        break;
      default:
        this.showCountrySelector = true;
        startCountry = 'United States';
        break;
    }

    this.form = this.fb.group({
      phone: ['', [Validators.required]],
      terms: ['', [Validators.requiredTrue]],
      newCompanyCampaign: ['', []],
      merchantName: [null, [Validators.required]],
      country: [startCountry, [Validators.required]],
      onTheGo: [false],
    });

    if (socialEmailEmpty) {
      this.form.addControl('email', this.fb.control('', [Validators.required, Validators.email]));
    }
  }

  parseFieldToPhoneNumber(value: ChangeData): string {
    return value?.e164Number ?? null;
  }

  submit() {
    this.submitAttempted = true;
    this.errorMsg = null;

    if (!this.form.valid) {
      this.errorMsg = ' ';
      return false;
    }

    this.loading = true;

    if (this.form.get('email')) {
      this.updateUserEmail();
    } else {
      this.createMerchant();
    }
  }

  updateUserEmail() {
    this.authService.updateUserEmail(this.form.get('email').value).subscribe((res) => {
      this.createMerchant();
    }, (err) => {
      this.errorMsg = err.errMsg;
      this.loading = false;
    });
  }

  createMerchant() {
    let originPayload = {};
    if (isPlatformBrowser(this.platformId)) {
      originPayload = this.toolsService.getMerchantOriginPayload();
    }
    let phoneNumber = this.form.get('phone').value === '' ? null : this.form.get('phone').value;
    phoneNumber = this.parseFieldToPhoneNumber(phoneNumber);

    const merchantPayload = {
      name: this.form.get('merchantName').value,
      country: this.form.get('country').value,
      onTheGo: this.form.get('onTheGo').value,
      timezone: this.currentTimezone,
      phoneNumber: phoneNumber,
      googlePlaceId: this.extraData?.place_id || null,
      currency: this.currencyService.getFromTimezone(this.currentTimezone),
      newCompanyCampaign: this.form.get('newCompanyCampaign').value ? true : false,
      settings: {
        showOnboard: true,
        showSetupGuide: {
          incomplete: true,
        }
      },
      ...originPayload
    };

    if (isPlatformBrowser(this.platformId)) {
      const referralCode = localStorage?.getItem('referral');
      if (referralCode) {
        merchantPayload['referredBy'] = referralCode;
      }
    }

    this.merchantsService.createMerchant(merchantPayload).subscribe(
      (res) => {
        localStorage?.removeItem('referral');
        // pre select first store
        this.analytics.trackEvent('create_merchant', 'engagement');
        this.analytics.trackProductAnalyticsEvent('create_merchant', {
          country: merchantPayload.country,
        });

        // if (typeof fbq === 'function') {
        //   fbq('trackSingle', environment.facebookPixel, 'CompleteRegistration');
        // }
        // removed to prevent duplicate with conversion API

        this.storeService.getMyStores().subscribe((resStores) => {
          const slug = resStores[0].slug;
          this.updateCurrentUserMerchantFlag(slug);
          this.router.navigateByUrl(`setup/merchant`);
        });
        this.authService.getCurrentUserDetails().subscribe();
      },
      (err) => {
        this.errorMsg = err.errMsg;
        this.loading = false;
      }
    );
  }

  updateCurrentUserMerchantFlag(slug) {
    const currentUserDetails = JSON.parse(localStorage?.getItem('currentUser'));
    const newDetails = { hasMerchant: true, merchantSlug: slug };
    const updatedUser = Object.assign(currentUserDetails, newDetails);
    this.userService.currentUser.next(updatedUser);
    localStorage?.setItem('currentUser', JSON.stringify(updatedUser));
  }

  handleGooglePlace(data) {
    setTimeout(() => {
      this.extraData = data;
      this.ref.detectChanges();
    });
  }

  confirmGoogle() {
    if (this.extraData.name) {
      this.form.patchValue({'merchantName': this.extraData.name});
    }
    if (this.extraData.international_phone_number) {
      this.form.patchValue({'phone': this.extraData.international_phone_number.replace(/\s/g, '')});
    }
    const country = this.extraData.address_components.find((c) => c.types.includes('country'));
    if (country) {
      this.form.patchValue({'country': country.long_name});
    }
    this.form.updateValueAndValidity();
    this.ref.detectChanges();
    this.currentStep = 2;
  }


}
